/* globals Promise */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import autobind from 'class-autobind';

import noop from 'lodash/noop';
import omit from 'lodash/omit';
import InputContainer from './input_container.jsx';
import Button from './btn_basic.jsx';

/*

  This component is for simplifying the creation of an inline editor inside
  of an InputContainer. i.e., for when an input container needs an "Edit" button,
  and an edit state with "Cancel" and "Commit" buttons.

  This component simplifies the state-management for that type of component
  such that the programmer does not need to re-implement onChange, onSave and onCancel
  functionality. 

  It uses render props so that the actual component used to render the non-editing
  and editing state is defined by the programmer.

  Note: as a general principal state should be lifted to higher and higher components,
  (all the way up to redux) if possible, but there really are instances where that 
  is unnecessary or conflicts with a legacy design.

*/

class InputContainerWithInlineEditing extends Component {

  constructor(props) {
    super(props);

    this.state = {
      isEditing: false,
      temporaryValue: null,
      error: null
    };

    autobind(this);
  }

  componentDidMount() {
    if (this.props.shouldEditOnMount) {
      this.handleStart();
    }
  }

  handleChange(value) {
    this.setState({
      temporaryValue: value,
      error: this.props.isLiveValidating ? this.props.getErrorMessageForValue(value) : null
    });
  }

  handleSave() {
    const { temporaryValue } = this.state;
    const error = this.props.getErrorMessageForValue(temporaryValue);
    if (error) {
      this.setState({
        error
      });

    } else {

      Promise.resolve(this.props.onSave(temporaryValue))

        .then(()=> {
          this.setState({
            error: null,
            isEditing: false
          });
        })
        .catch(()=> {
          this.setState({
            error: 'An error occurred while saving.'
          });
        });
    }
  }

  handleCancel() {
    this.setState({ isEditing:false, temporaryValue:null, error:null });
  }

  handleStart() {
    this.setState({ isEditing:true, temporaryValue:this.props.value });
  }

  render() {

    const {
      editButtonText,
      cancelButtonText,
      saveButtonText,
      editButtonClassName,
      saveButtonClassName,
      cancelButtonClassName,
      extra,
      render,
      shouldRenderCancelButton,
      shouldRenderEditButton,
      shouldRenderSaveButton,
      isEmptyValueAllowed,
      value,
      ...props
    } = omit(this.props, ['getErrorMessageForValue', 'isLiveValidating', 'onSave']);

    const {
      error,
      temporaryValue,
      isEditing
    } = this.state;

    const cancelButton = (
      <Button
        secondary
        className={cancelButtonClassName}
        onClick={this.handleCancel}
      >
        { cancelButtonText }
      </Button>
    );

    const saveButton = (
      <Button
        disabled={isEmptyValueAllowed ?  false  : !temporaryValue}
        className={saveButtonClassName}
        onClick={this.handleSave}
      >
        { saveButtonText }
      </Button>
    );

    const editButton = (
      <Button 
        secondary
        className={editButtonClassName}
        onClick={this.handleStart}
      >
        {editButtonText}
      </Button>
    );
    return (
      <InputContainer 
        isAllowedEmpty={true}
        error={error}
        extra={
          <Fragment>
          {extra}
          {
            isEditing ?
              <Fragment>
                {shouldRenderCancelButton ? cancelButton : null}
                {shouldRenderSaveButton ? saveButton : null}
              </Fragment>
            :
              shouldRenderEditButton ? editButton : null
          }
          </Fragment>
        }
        {...props}
      >
      { 
        render({
          value: isEditing ? temporaryValue : value, 
          isEditing, 
          cancelButton,
          editButton,
          saveButton,
          onEdit: this.handleStart,
          onSave: this.handleSave,
          onCancel: this.handleCancel,
          onValueChange: this.handleChange
        }) 
      }
      </InputContainer>
    );


  }
}

InputContainerWithInlineEditing.propTypes = {
  editButtonText: PropTypes.node,
  cancelButtonText: PropTypes.node,
  saveButtonText: PropTypes.node,
  editButtonClassName: PropTypes.string,
  saveButtonClassName: PropTypes.string,
  cancelButtonClassName: PropTypes.string,
  shouldRenderCancelButton: PropTypes.bool,
  shouldRenderEditButton: PropTypes.bool,
  shouldRenderSaveButton: PropTypes.bool,
  getErrorMessageForValue: PropTypes.func,
  isLiveValidating: PropTypes.bool,
  isEmptyValueAllowed: PropTypes.bool,
  shouldEditOnMount: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  render: PropTypes.func.isRequired // Render prop
};

InputContainerWithInlineEditing.defaultProps = {
  editButtonText: 'Edit',
  cancelButtonText: 'Cancel',
  saveButtonText: 'Save',
  isLiveValidating: false,
  isEmptyValueAllowed: true,
  shouldEditOnMount: false,
  getErrorMessageForValue: noop,
  shouldRenderCancelButton: true,
  shouldRenderEditButton: true,
  shouldRenderSaveButton: true
};

export default InputContainerWithInlineEditing;
